import {
  DockLayoutContractError,
} from "./errors";


export const DOCK_NODE_TYPES = Object.freeze({
  PANEL: "panel",
  TABS: "tabs",
  SPLIT: "split",
});


export const DOCK_SPLIT_ORIENTATIONS = Object.freeze({
  HORIZONTAL: "horizontal",
  VERTICAL: "vertical",
});


function normalizeId(value, fieldName) {
  if (typeof value !== "string" || value.trim() === "") {
    throw new DockLayoutContractError(
      `${fieldName} must be a non-empty string.`,
    );
  }

  return value.trim();
}


function freezeArray(values) {
  return Object.freeze([...values]);
}


export function createDockPanelNode({
  id,
  panelId,
} = {}) {
  return Object.freeze({
    type: DOCK_NODE_TYPES.PANEL,
    id: normalizeId(id, "DockPanelNode.id"),
    panelId: normalizeId(
      panelId,
      "DockPanelNode.panelId",
    ),
  });
}


export function createDockTabNode({
  id,
  panelIds,
  activePanelId = null,
} = {}) {
  if (!Array.isArray(panelIds) || panelIds.length === 0) {
    throw new DockLayoutContractError(
      "DockTabNode.panelIds must contain at least one panel.",
    );
  }

  const normalizedPanelIds = panelIds.map(
    (panelId) => normalizeId(
      panelId,
      "DockTabNode.panelIds[]",
    ),
  );

  if (
    new Set(normalizedPanelIds).size
    !== normalizedPanelIds.length
  ) {
    throw new DockLayoutContractError(
      "DockTabNode.panelIds must be unique.",
      "DUPLICATE_DOCK_PANEL_ID",
    );
  }

  const normalizedActivePanelId = activePanelId === null
    ? normalizedPanelIds[0]
    : normalizeId(
      activePanelId,
      "DockTabNode.activePanelId",
    );

  if (!normalizedPanelIds.includes(normalizedActivePanelId)) {
    throw new DockLayoutContractError(
      "DockTabNode.activePanelId must reference panelIds.",
      "UNKNOWN_ACTIVE_DOCK_PANEL",
    );
  }

  return Object.freeze({
    type: DOCK_NODE_TYPES.TABS,
    id: normalizeId(id, "DockTabNode.id"),
    panelIds: freezeArray(normalizedPanelIds),
    activePanelId: normalizedActivePanelId,
  });
}


export function createDockSplitNode({
  id,
  orientation,
  children,
  sizes = null,
} = {}) {
  if (
    orientation !== DOCK_SPLIT_ORIENTATIONS.HORIZONTAL
    && orientation !== DOCK_SPLIT_ORIENTATIONS.VERTICAL
  ) {
    throw new DockLayoutContractError(
      "DockSplitNode.orientation must be horizontal or vertical.",
    );
  }

  if (!Array.isArray(children) || children.length < 2) {
    throw new DockLayoutContractError(
      "DockSplitNode.children must contain at least two nodes.",
    );
  }

  const validatedChildren = children.map(assertDockNode);

  const normalizedSizes = sizes === null
    ? validatedChildren.map(
      () => 100 / validatedChildren.length,
    )
    : sizes.map(Number);

  if (
    normalizedSizes.length !== validatedChildren.length
    || normalizedSizes.some(
      (size) => !Number.isFinite(size) || size <= 0,
    )
  ) {
    throw new DockLayoutContractError(
      "DockSplitNode.sizes must contain one positive size per child.",
    );
  }

  return Object.freeze({
    type: DOCK_NODE_TYPES.SPLIT,
    id: normalizeId(id, "DockSplitNode.id"),
    orientation,
    children: freezeArray(validatedChildren),
    sizes: freezeArray(normalizedSizes),
  });
}


export function isDockNode(value) {
  try {
    assertDockNode(value);
    return true;
  } catch {
    return false;
  }
}


export function assertDockNode(value) {
  if (!value || typeof value !== "object") {
    throw new DockLayoutContractError(
      "Dock node must be an object.",
    );
  }

  if (value.type === DOCK_NODE_TYPES.PANEL) {
    return createDockPanelNode(value);
  }

  if (value.type === DOCK_NODE_TYPES.TABS) {
    return createDockTabNode(value);
  }

  if (value.type === DOCK_NODE_TYPES.SPLIT) {
    return createDockSplitNode(value);
  }

  throw new DockLayoutContractError(
    `Unsupported dock node type: ${String(value.type)}.`,
    "UNKNOWN_DOCK_NODE_TYPE",
  );
}


function collectPanelIds(node, target = []) {
  if (node.type === DOCK_NODE_TYPES.PANEL) {
    target.push(node.panelId);
    return target;
  }

  if (node.type === DOCK_NODE_TYPES.TABS) {
    target.push(...node.panelIds);
    return target;
  }

  for (const child of node.children) {
    collectPanelIds(child, target);
  }

  return target;
}


export function createDockLayout({
  id,
  root,
  hiddenPanelIds = [],
  schemaVersion = 1,
} = {}) {
  const normalizedRoot = assertDockNode(root);

  if (
    !Number.isInteger(schemaVersion)
    || schemaVersion <= 0
  ) {
    throw new DockLayoutContractError(
      "DockLayout.schemaVersion must be a positive integer.",
    );
  }

  if (!Array.isArray(hiddenPanelIds)) {
    throw new DockLayoutContractError(
      "DockLayout.hiddenPanelIds must be an array.",
    );
  }

  const visiblePanelIds = collectPanelIds(normalizedRoot);
  const normalizedHiddenPanelIds = hiddenPanelIds.map(
    (panelId) => normalizeId(
      panelId,
      "DockLayout.hiddenPanelIds[]",
    ),
  );

  const allPanelIds = [
    ...visiblePanelIds,
    ...normalizedHiddenPanelIds,
  ];

  if (new Set(allPanelIds).size !== allPanelIds.length) {
    throw new DockLayoutContractError(
      "Each panel may appear only once in a DockLayout.",
      "DUPLICATE_DOCK_PANEL_REFERENCE",
    );
  }

  return Object.freeze({
    id: normalizeId(id, "DockLayout.id"),
    root: normalizedRoot,
    hiddenPanelIds: freezeArray(normalizedHiddenPanelIds),
    schemaVersion,
  });
}


export function isDockLayout(value) {
  try {
    assertDockLayout(value);
    return true;
  } catch {
    return false;
  }
}


export function assertDockLayout(value) {
  if (!value || typeof value !== "object") {
    throw new DockLayoutContractError(
      "Expected a DockLayout object.",
    );
  }

  return createDockLayout(value);
}


export function serializeDockLayout(layout) {
  const validatedLayout = assertDockLayout(layout);

  return JSON.stringify(validatedLayout);
}


export function deserializeDockLayout(serializedLayout) {
  if (typeof serializedLayout !== "string") {
    throw new DockLayoutContractError(
      "Serialized DockLayout must be a string.",
    );
  }

  let parsed;

  try {
    parsed = JSON.parse(serializedLayout);
  } catch {
    throw new DockLayoutContractError(
      "Serialized DockLayout contains invalid JSON.",
      "INVALID_DOCK_LAYOUT_JSON",
    );
  }

  return createDockLayout(parsed);
}
